import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createPresetQuoteTemplate,
  deletePresetQuoteTemplate,
  getPresetQuoteDetail,
  getPresetQuoteList,
  updatePresetQuoteTemplate,
} from "./thunk";
import {
  ApiResponse,
  PaginatedPayload,
  PaginationMeta,
} from "types/ApiResponse";
import { PresetQuotePayload } from "types/preset.api";

interface PresetQuoteState {
  message: string | null;
  isLoading: boolean;
  presetQuoteList: PresetQuotePayload[];
  success: boolean;
  error: boolean;
  meta: PaginationMeta | null;
  presetQuoteDetail: PresetQuotePayload | null;
}

export const initialState: PresetQuoteState = {
  message: null,
  isLoading: false,
  presetQuoteList: [],
  success: false,
  error: false,
  meta: null,
  presetQuoteDetail: null,
};

const presetSlice = createSlice({
  name: "preset",
  initialState,
  reducers: {
    clearPresetQuoteDetail: (state) => {
      state.presetQuoteDetail = null;
    },
  },
  extraReducers: (builder) => {
    //Preset Quote List
    // ---------- Pending ----------
    builder.addCase(getPresetQuoteList.pending, (state) => {
      state.isLoading = true;
      state.error = false;
      state.success = false;
    });

    // ---------- Fulfilled ----------
    builder.addCase(
      getPresetQuoteList.fulfilled,
      (
        state,
        action: PayloadAction<ApiResponse<PaginatedPayload<PresetQuotePayload>>>
      ) => {
        state.isLoading = false;
        state.error = false;
        state.success = true;
        const newItems = action?.payload?.payload?.data;
        state.presetQuoteList = newItems;
        state.meta = action?.payload?.payload?.meta;
      }
    );

    // ---------- Rejected ----------
    builder.addCase(getPresetQuoteList.rejected, (state) => {
      state.isLoading = false;
      state.error = true;
      state.success = false;
    });

    //Preset Quote Detail
    // ---------- Pending ----------
    builder.addCase(getPresetQuoteDetail.pending, (state) => {
      state.isLoading = true;
      state.error = false;
      state.success = false;
    });

    // ---------- Fulfilled ----------
    builder.addCase(
      getPresetQuoteDetail.fulfilled,
      (state, action: PayloadAction<ApiResponse<PresetQuotePayload>>) => {
        state.isLoading = false;
        state.error = false;
        state.success = true;
        state.presetQuoteDetail = action?.payload?.payload;
      }
    );

    // ---------- Rejected ----------
    builder.addCase(getPresetQuoteDetail.rejected, (state) => {
      state.isLoading = false;
      state.error = true;
      state.success = false;
    });

    //Create Preset Quote
    // ---------- Pending ----------
    builder.addCase(createPresetQuoteTemplate.pending, (state) => {
      state.isLoading = true;
      state.error = false;
      state.success = false;
    });

    // ---------- Fulfilled ----------
    builder.addCase(
      createPresetQuoteTemplate.fulfilled,
      (state, action: PayloadAction<ApiResponse<PresetQuotePayload>>) => {
        state.isLoading = false;
        state.error = false;
        state.success = true;
        state.presetQuoteList = [
          ...state.presetQuoteList,
          action?.payload?.payload,
        ];
        if (state.meta) {
          state.meta.to++;
          state.meta.total++;
          state.meta.last_page = Math.ceil(
            state.meta.total / state.meta.per_page
          );
        }
      }
    );

    // ---------- Rejected ----------
    builder.addCase(createPresetQuoteTemplate.rejected, (state) => {
      state.isLoading = false;
      state.error = true;
      state.success = false;
    });

    // Update Preset Quotes
    // ---------- Pending ----------
    builder.addCase(updatePresetQuoteTemplate.pending, (state) => {
      state.isLoading = true;
      state.error = false;
      state.success = false;
    });

    // ---------- Fulfilled ----------
    builder.addCase(
      updatePresetQuoteTemplate.fulfilled,
      (state, action: PayloadAction<ApiResponse<PresetQuotePayload>>) => {
        state.isLoading = false;
        state.error = false;
        state.success = true;
        const ind = state.presetQuoteList.findIndex(
          (item) => item.id === action?.payload?.payload.id
        );
        state.presetQuoteList[ind] = action?.payload?.payload;
      }
    );

    // ---------- Rejected ----------
    builder.addCase(deletePresetQuoteTemplate.rejected, (state) => {
      state.isLoading = false;
      state.error = true;
      state.success = false;
    });

    // Update Preset Quotes
    // ---------- Pending ----------
    builder.addCase(deletePresetQuoteTemplate.pending, (state) => {
      state.isLoading = true;
      state.error = false;
      state.success = false;
    });

    // ---------- Fulfilled ----------
    builder.addCase(
      deletePresetQuoteTemplate.fulfilled,
      (state, action: PayloadAction<{ templateId: number }>) => {
        state.isLoading = false;
        state.error = false;
        state.success = true;
        state.presetQuoteList = state.presetQuoteList.filter(
          (item) => item.id !== action.payload.templateId
        );
        if (state.meta) {
          state.meta.to--;
          state.meta.total--;
          state.meta.last_page = Math.ceil(
            state.meta.total / state.meta.per_page
          );
        }
      }
    );

    // ---------- Rejected ----------
    builder.addCase(updatePresetQuoteTemplate.rejected, (state) => {
      state.isLoading = false;
      state.error = true;
      state.success = false;
    });
  },
});

export const { clearPresetQuoteDetail } = presetSlice.actions;

export default presetSlice.reducer;
